/**
 * Persisted DataTable view mode (list / cards) synced with preferences.
 */

import * as React from 'react';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { updateInterfaceConfig } from '@/services/preferences.service';
import type { ViewMode } from '@/types/data-table.types';

/** Options for {@link useViewMode} (table key + default). */
export interface UseViewModeOptions {
  tableName: string;
  defaultMode?: ViewMode;
}

/** Persist list/cards `defaultView` on the named interface config. */
export const useViewMode = (tableName: string, defaultMode: ViewMode = 'list') => {
  const { getInterfaceConfig, updateInterfaceConfig: updateStore } = usePreferencesStore();
  const [isSaving, setIsSaving] = React.useState(false);

  const tableConfig = getInterfaceConfig(tableName);

  // Sync from the persisted Zustand store so the first paint does not flip
  // list→cards and fire a second list request.
  const [viewMode, setViewMode] = React.useState<ViewMode>(() => {
    const config = usePreferencesStore.getState().getInterfaceConfig(tableName);
    if (config && typeof config === 'object' && 'defaultView' in config) {
      const savedMode = (config as { defaultView?: unknown }).defaultView;
      if (savedMode === 'list' || savedMode === 'cards') return savedMode;
    }
    return defaultMode;
  });

  // Re-sync if backend preferences arrive after the first render.
  React.useEffect(() => {
    if (tableConfig && typeof tableConfig === 'object' && 'defaultView' in tableConfig) {
      const savedMode = tableConfig.defaultView;
      if (savedMode === 'list' || savedMode === 'cards') {
        setViewMode(savedMode);
      }
    }
  }, [tableConfig]);

  const changeViewMode = React.useCallback(
    async (mode: ViewMode) => {
      setViewMode(mode);
      setIsSaving(true);

      const currentConfig = (
        tableConfig && typeof tableConfig === 'object' ? tableConfig : {}
      ) as Record<string, unknown>;
      updateStore(tableName, { ...currentConfig, defaultView: mode });

      try {
        await updateInterfaceConfig(tableName, { defaultView: mode });
      } catch (error) {
        console.error('Failed to save view mode:', error);
      } finally {
        setIsSaving(false);
      }
    },
    [tableName, tableConfig, updateStore],
  );

  return {
    viewMode,
    setViewMode: changeViewMode,
    isLoading: false,
    isSaving,
  };
};
